import React, { useEffect, useMemo, useRef } from "react";
import styles from "./otpBoxInput.module.scss";

/**
 * Segmented one-time-code input: N single-character boxes driven by one string `value`.
 *
 * The reference login screen (refference-pages-and-components/login/Login.jsx) imports a component
 * of this name but the file does not exist in this repo, so it is written here to that call
 * signature - `value` / `onChange` / `numInputs` - rather than inventing a different one.
 *
 * Deliberate behaviours:
 * - The parent owns the value. Boxes are a rendering of `value.split("")`, never separate state,
 *   so a failed verification can reset the whole field with `setOtp("")`.
 * - Digits only, because §3.1 mints a numeric OTP (`app.auth.otp.length=6`).
 * - Paste anywhere in the row fills forward from that box, so pasting a code from an email works
 *   whichever box has focus.
 * - `inputMode="numeric"` + `autoComplete="one-time-code"` lets iOS/Android offer the SMS/email
 *   code straight from the keyboard.
 */
export default function OtpBoxInput({
  value = "",
  onChange = () => {},
  numInputs = 6,
  disabled = false,
  autoFocus = true,
  ariaLabel = "One-time code",
}) {
  const inputsRef = useRef([]);
  const digits = useMemo(() => {
    const chars = String(value).slice(0, numInputs).split("");
    return Array.from({ length: numInputs }, (_, i) => chars[i] || "");
  }, [value, numInputs]);

  useEffect(() => {
    if (autoFocus && !disabled) inputsRef.current[0]?.focus();
  }, [autoFocus, disabled]);

  const commit = (nextDigits, focusIndex) => {
    onChange(nextDigits.join("").replace(/\D/g, ""));
    if (focusIndex != null) {
      const clamped = Math.max(0, Math.min(numInputs - 1, focusIndex));
      inputsRef.current[clamped]?.focus();
      inputsRef.current[clamped]?.select?.();
    }
  };

  const handleChange = (index, raw) => {
    const typed = raw.replace(/\D/g, "");
    if (!typed) {
      const next = [...digits];
      next[index] = "";
      commit(next);
      return;
    }
    // Typing (or an autofilled code) can deliver more than one character at once - spread it
    // across the following boxes instead of dropping everything after the first.
    const next = [...digits];
    let cursor = index;
    typed.split("").forEach((char) => {
      if (cursor < numInputs) {
        next[cursor] = char;
        cursor += 1;
      }
    });
    commit(next, cursor);
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = "";
        commit(next, index);
      } else {
        next[Math.max(0, index - 1)] = "";
        commit(next, index - 1);
      }
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (index, event) => {
    const pasted = (event.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (!pasted) return;
    event.preventDefault();
    const next = [...digits];
    let cursor = index;
    pasted.split("").forEach((char) => {
      if (cursor < numInputs) {
        next[cursor] = char;
        cursor += 1;
      }
    });
    commit(next, cursor);
  };

  return (
    <div className={styles.boxes} role="group" aria-label={ariaLabel}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          className={styles.box}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={numInputs}
          value={digit}
          disabled={disabled}
          aria-label={`${ariaLabel} digit ${index + 1}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}
